// Telegram hands a Mini App exactly one `startapp` value, surfaced as
// `start_param`. This module decides what a given value means.
//
// Historically it only ever meant "referral code". It now also names a screen,
// so that `t.me/<bot>?startapp=deposit` opens the deposit page — which means
// the two meanings share one namespace and something has to tell them apart.

/**
 * Screens reachable as `t.me/<bot>?startapp=<key>`.
 *
 * An allowlist, not a path pass-through: `start_param` is attacker-supplied
 * and routing on it directly would let any link drop a user anywhere in the
 * app. Add a line here to add a target.
 *
 * Keys are matched case-insensitively against the trimmed value. Keep them to
 * lowercase words that could not plausibly be issued as an invitation code —
 * every key here is a value that can no longer function as a referral, since a
 * value cannot mean both things at once.
 */
const DEEP_LINKS: Readonly<Record<string, string>> = {
  deposit: '/deposit',
  withdraw: '/withdraw',
  // The agent's own invitation links, not the referral hub above them.
  // Two spellings reach it: `invitation` is the one to hand out, and
  // `mine-referral` stays so links already shared under it keep working.
  invitation: '/mine/referral/invitation',
  'mine-referral': '/mine/referral/invitation',
};

/**
 * The in-app path this `start_param` names, or null when it names no screen.
 *
 * Null is the ordinary answer — most values are referral codes.
 */
export function deepLinkPath(startParam: string | null | undefined): string | null {
  if (!startParam) return null;
  return DEEP_LINKS[startParam.trim().toLowerCase()] ?? null;
}

/**
 * Namespace the bot uses when it forwards an invitation, as in
 * `invite-REFOPcENC1`. Only the prefix is matched case-insensitively — the
 * code itself is case-SENSITIVE and passes through untouched.
 */
const INVITE_PREFIX = 'invite-';

/**
 * The referral code this `start_param` carries, or null when it names a screen.
 *
 * Every site that treats `start_param` as an invitation goes through this, so
 * a deep link is never stored as a referral code, prefilled into the sign-up
 * form, or sent to the backend as an `invitation` the user never had.
 *
 * Two spellings of an invitation arrive here. A bare code is what Mini App
 * referral links have always carried. A namespaced `invite-<CODE>` is what the
 * bot forwards when the user came in through its own `?start=invite-<CODE>`
 * link — that parameter goes to the bot, never to this app, so the bot has to
 * hand the code on through `startapp` for it to reach us at all. The prefix is
 * stripped here so the backend receives the same bare code either way.
 */
export function referralCodeFromStartParam(
  startParam: string | null | undefined,
): string | null {
  if (!startParam) return null;
  if (deepLinkPath(startParam)) return null;

  const value = startParam.trim();
  if (value.toLowerCase().startsWith(INVITE_PREFIX)) {
    // A bare `invite-` names no code; treat it as no invitation rather than
    // sending the backend an empty string.
    return value.slice(INVITE_PREFIX.length) || null;
  }

  // Unprefixed codes pass through exactly as they arrived — untrimmed, as
  // every caller has always received them.
  return startParam;
}
